import React, { Fragment } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { X } from "lucide-react";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  leftExtend?: React.ReactNode;
  rightExtend?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  position?: "center" | "right" | "left";
  showHeader?: boolean;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  className?: string;
  nonClosable?: boolean;
  zIndex?: number;
}

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  full: "max-w-full",
};

const positionClasses = {
  center: "flex items-center justify-center",
  right: "flex items-center justify-end",
  left: "flex items-center justify-start",
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  leftExtend,
  rightExtend,
  size = "md",
  position = "center",
  showHeader = true,
  showCloseButton = true,
  closeOnBackdropClick = false,
  className = "",
  nonClosable = false,
  zIndex = 20,
}) => {
  // Calculate panel style including box shadow
  const getPanelStyle = (): React.CSSProperties => {
    return {
      boxShadow: "0px 9px 7px rgba(0, 0, 0, 0.1)", // Custom box-shadow
    };
  };

  const effectiveShowCloseButton = nonClosable ? false : showCloseButton;
  const effectiveCloseOnBackdropClick = nonClosable ? false : closeOnBackdropClick;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative"
        style={{ zIndex: zIndex }}
        onClose={effectiveCloseOnBackdropClick ? onClose : () => {}}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </TransitionChild>

        <div className="fixed inset-0 h-full">
          <div
            className={`flex h-full ${positionClasses[position]} ${
              position === "center" ? "p-4" : ""
            }`}
          >
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className={`relative ${
                  className || `w-full ${sizeClasses[size]} h-auto max-h-[90vh]`
                } bg-white border border-gray-200 flex flex-col ${
                  position === "right"
                    ? "rounded-l-2xl"
                    : position === "left"
                      ? "rounded-r-2xl"
                      : "rounded-2xl"
                } ${!className && (position === "right" || position === "left") ? "h-full" : ""}`}
                style={getPanelStyle()}
              >
                {/* Header - Fixed */}
                {showHeader ? (
                  <div className="mx-5 py-5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div>{leftExtend}</div>

                    <div className="flex-1 text-center mx-5">
                      <DialogTitle as="h3" className="text-xl font-bold text-gray-900">
                        {title}
                      </DialogTitle>
                      {subtitle && <div className="text-sm text-gray-500 mt-1">{subtitle}</div>}
                    </div>

                    <div className="flex items-center space-x-2">
                      <div>{rightExtend}</div>
                      {effectiveShowCloseButton ? (
                        <button
                          className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                          onClick={onClose}
                        >
                          <X className="w-5 h-5 text-gray-500" />
                        </button>
                      ) : (
                        <div className="w-7 h-7" />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mx-5 py-2 flex items-center justify-between flex-shrink-0">
                    <div></div>
                    <div className="flex items-center space-x-2">
                      <div>{rightExtend}</div>
                      {effectiveShowCloseButton ? (
                        <button
                          className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                          onClick={onClose}
                        >
                          <X className="w-5 h-5 text-gray-500" />
                        </button>
                      ) : (
                        <div className="w-7 h-7" />
                      )}
                    </div>
                  </div>
                )}

                {/* Body - Scrollable */}
                <div className="flex-1 overflow-y-auto">{children}</div>

                {/* Footer - Fixed */}
                {footer && (
                  <div
                    className={`px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 ${
                      position === "right"
                        ? "rounded-bl-2xl"
                        : position === "left"
                          ? "rounded-br-2xl"
                          : "rounded-b-2xl"
                    }`}
                  >
                    {footer}
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
